import React from 'react';
import withStyles from 'isomorphic-style-loader/withStyles';
import s from './CompanyProjects.css';
import lpApi from '../../helpers/lpApi';
import ProjectThread from '../../components/ProjectThread/ProjectThread';
import ProjectContract from '../../components/ProjectContract/ProjectContract';
import CompanyAuth from '../become-a-company/CompanyAuth';

// =============================================================================
// CompanyProjects — COMPANY-side web for the Custom Projects (Gros Projets)
// module: marketplace of masked published projects, project detail + line-item
// quote, and "My quotes". All backend ops live on besthand-api and are reached
// through the `/lp-graphql` proxy via the `lpApi` helper. Single page, three
// internal views ('marketplace' | 'detail' | 'quotes').
//
// GATE: a logged-out caller (proxy 401 on myCompanyProfile) OR a caller whose
// myCompanyProfile is empty / not KYB-APPROVED is shown the SAME detached
// CompanyAuth surface used by /become-a-company. After sign-in we re-bootstrap.
// Reusing CompanyAuth keeps the id_token-cookie auth identical across surfaces.
//
// SSR-safe: window/localStorage are only touched inside lifecycle/handlers
// (componentDidMount, click handlers), never during module load or render.
// =============================================================================

// ---- GraphQL operations (against besthand-api ProjectsResponseType) ----

const Q_MY_COMPANY = `
  query {
    myCompanyProfile {
      status errorMessage
      companyProfile { id legalName kybStatus }
    }
  }
`;

const Q_DASHBOARD = `
  query {
    companyDashboard {
      status errorMessage
      companyProfile { id legalName kybStatus operatingStates specialties }
      quotes {
        id projectRequestId status anonymousRef
        subtotal total totalDurationDays
        submittedAt selectedAt createdAt
      }
      contracts {
        id projectRequestId projectQuoteId status
        totalAmount downPaymentAmount currency
        cancellationDeadline signedAtCompany signedAtClient
        completedAt cancelledAt createdAt
      }
      milestones {
        id projectContractId orderIndex name amount currency
        isDownPayment status
        startedAt submittedAt approvedAt paidAt
      }
      payouts {
        id projectContractId projectMilestoneId status
        amount platformFeeCompany reversedAmount currency
        transferredAt createdAt
      }
    }
  }
`;

const Q_MARKETPLACE = `
  query ($category: String, $state: String) {
    marketplaceProjectRequests(category: $category, state: $state) {
      status errorMessage eligible eligibilityReasons
      projectRequests {
        id anonymousClientRef category subcategory title
        siteCity siteState siteZip
        budgetMin budgetMax budgetIsFirm
        ambitionLevel completenessScore timelineJson
        publishedAt
        mediaAssets { id kind }
      }
    }
  }
`;

const Q_PROJECT = `
  query ($projectRequestId: String!) {
    projectRequest(projectRequestId: $projectRequestId) {
      status errorMessage eligible eligibilityReasons
      projectRequest {
        id anonymousClientRef category subcategory propertyType propertyYearBuilt
        title description ambitionLevel hasPlans hasPermit
        siteCity siteState siteZip
        budgetMin budgetMax budgetIsFirm
        measurementsJson intakeAnswersJson timelineJson categorySpecificDataJson
        completenessScore publishedAt
        mediaAssets { id kind slot cdnUrl thumbnailUrl storageKey }
      }
    }
  }
`;

const Q_MY_QUOTES = `
  query {
    myQuotes {
      status errorMessage
      quotes {
        id projectRequestId status anonymousRef
        subtotal total platformCommissionClient
        totalDurationDays validUntil submittedAt createdAt
      }
    }
  }
`;

// E4 — commission rates (decimals) + escrow payment-mode config. Both readable
// by any authenticated user; we use them to show the company a live two-sided
// breakdown (client pays / you receive) and a payment-mode hint before submit.
const Q_COMMISSIONS = `
  query {
    projectCommissions {
      status errorMessage
      projectCommissionClient projectCommissionPro
    }
  }
`;

const Q_ESCROW = `
  query {
    escrowSettings {
      status errorMessage
      escrowThresholdUsd escrowCategoryOverridesJson
    }
  }
`;

const Q_NOTIFICATIONS = `
  query {
    companyNotifications {
      status errorMessage unreadCount
      companyNotifications { id type title body projectRequestId projectContractId isRead createdAt }
    }
  }
`;

const Q_DOCUMENTS = `
  query {
    companyDocuments {
      status errorMessage newDocumentsCount
      companyDocuments { kind title subtitle url projectContractId createdAt isNew }
    }
  }
`;

const M_MARK_NOTIF_READ = `mutation { markCompanyNotificationsRead { status errorMessage unreadCount } }`;
const M_MARK_DOCS_SEEN = `mutation { markCompanyDocumentsSeen { status errorMessage newDocumentsCount } }`;

const M_CREATE_QUOTE = `
  mutation ($projectRequestId: String!) {
    createQuote(projectRequestId: $projectRequestId) {
      status errorMessage quote { id status projectRequestId }
    }
  }
`;

const M_UPDATE_QUOTE = `
  mutation (
    $quoteId: String!, $laborItemsJson: String, $materialsItemsJson: String,
    $milestonesProposalJson: String, $totalDurationDays: Int, $validUntil: String,
    $subtotal: Float
  ) {
    updateQuote(
      quoteId: $quoteId, laborItemsJson: $laborItemsJson,
      materialsItemsJson: $materialsItemsJson, milestonesProposalJson: $milestonesProposalJson,
      totalDurationDays: $totalDurationDays, validUntil: $validUntil, subtotal: $subtotal
    ) {
      status errorMessage
      quote { id status subtotal total platformCommissionClient }
    }
  }
`;

const M_SUBMIT_QUOTE = `
  mutation ($quoteId: String!) {
    submitQuote(quoteId: $quoteId) {
      status errorMessage validationErrors
      quote { id status submittedAt total }
    }
  }
`;

// AI quote-drafting assistant (supply side). DRAFT ONLY — it never creates,
// updates, or submits a quote. We pre-fill the EXISTING quote-form state from
// the returned scope / line items / milestones; the company edits everything,
// then the normal createQuote -> updateQuote -> submitQuote flow runs unchanged
// (submitQuote still enforces §7159). Goes through the same lpApi/`/lp-graphql`
// proxy as every other LP call.
const M_AI_QUOTE_DRAFT = `
  mutation ($projectRequestId: String!) {
    aiQuoteDraft(projectRequestId: $projectRequestId) {
      status errorMessage
      scopeDescription lineItems milestones summary followUpQuestion
    }
  }
`;

// ---- helpers ----

const CATEGORY_LABEL = {
  KITCHEN: 'Kitchen',
  BATHROOM: 'Bathroom',
  BASEMENT: 'Basement',
  EXTERIOR: 'Exterior',
  ROOFING: 'Roofing',
  INSTALLATION: 'Installation',
  OTHER: 'Other',
};

const AMBITION_LABEL = {
  ECONOMIC: 'Economic',
  STANDARD: 'Standard',
  PREMIUM: 'Premium',
  LUXURY: 'Luxury',
};

function money(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  return `$${v.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

function budgetRange(min, max) {
  if (min == null && max == null) return 'Budget not specified';
  if (min != null && max != null) return `${money(min)} – ${money(max)}`;
  return min != null ? `From ${money(min)}` : `Up to ${money(max)}`;
}

function safeParse(json) {
  if (!json) return null;
  try { return typeof json === 'string' ? JSON.parse(json) : json; } catch (e) { return null; }
}

// Human label for a status enum (e.g. IN_CANCELLATION_PERIOD -> "In cancellation period").
function statusLabel(raw) {
  if (!raw) return '—';
  const t = String(raw).replace(/_/g, ' ').toLowerCase();
  return t.charAt(0).toUpperCase() + t.slice(1);
}

// CSS-module key suffix for a status pill, e.g. 'IN_PROGRESS' -> 'in_progress'.
function statusKey(raw) {
  return String(raw || 'unknown').toLowerCase();
}

// Client identity is ALWAYS masked on the company side: we never surface the
// contract.clientUserId. Use the contract's own id (or the linked project /
// quote ref) so the company sees a stable anonymous reference instead.
function maskedContractRef(c) {
  if (!c) return 'Client';
  return `Project #${c.projectRequestId || c.id}`;
}

function shortDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const Icon = ({ d }) => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d={d} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ICONS = {
  pin: 'M12 21s7-6 7-11a7 7 0 1 0-14 0c0 5 7 11 7 11Z M12 10a1 1 0 1 0 0 0',
  wallet: 'M3 10h18M5 6h14a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2Z',
  clock: 'M12 7v5l3 2 M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Z',
  photo: 'M3 5h18v14H3zM3 16l5-5 4 4 3-3 6 6',
  gauge: 'M12 13a3 3 0 1 0 0-6 M5 19a8 8 0 1 1 14 0',
  check: 'M20 6 9 17l-5-5',
  back: 'M15 18l-6-6 6-6',
  doc: 'M14 3v5h5 M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-5Z',
  box: 'M21 16V8l-9-5-9 5v8l9 5 9-5Z M3 8l9 5 9-5 M12 13v8',
  shield: 'M12 2 4 6v6c0 5 3.5 8 8 10 4.5-2 8-5 8-10V6l-8-4Z',
  dollar: 'M12 1v22 M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6',
  layers: 'M12 2 2 7l10 5 10-5-10-5Z M2 17l10 5 10-5 M2 12l10 5 10-5',
  briefcase: 'M3 8h18v11a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V8Z M8 8V6a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2',
  trend: 'M3 17l6-6 4 4 8-8 M21 7v6h-6',
};

// Empty-state illustration — a self-contained inline brand SVG (friendly
// toolbox), so the page never depends on an external asset that may be missing.
const EmptyMascot = ({ className }) => (
  <svg className={className} viewBox="0 0 96 96" fill="none" aria-hidden="true">
    <rect x="16" y="34" width="64" height="40" rx="8" fill="#fff0ef" stroke="#e8232b" strokeWidth="3" />
    <path d="M16 50h64" stroke="#e8232b" strokeWidth="3" />
    <rect x="40" y="46" width="16" height="10" rx="3" fill="#fff" stroke="#e8232b" strokeWidth="3" />
    <path d="M34 34v-6a6 6 0 0 1 6-6h16a6 6 0 0 1 6 6v6" stroke="#e8232b" strokeWidth="3" strokeLinecap="round" />
    <circle cx="38" cy="64" r="2.5" fill="#e8232b" />
    <circle cx="58" cy="64" r="2.5" fill="#e8232b" />
    <path d="M40 70q8 5 16 0" stroke="#e8232b" strokeWidth="3" strokeLinecap="round" />
  </svg>
);

class CompanyProjects extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      authed: false,
      eligibleCompany: false, // logged in AND KYB-approved company
      error: '',
      // view: 'dashboard' | 'marketplace' | 'detail' | 'quotes' | 'notifications' | 'documents'
      view: 'dashboard',
      company: null,
      // bell + mail
      notifications: [],
      unreadCount: 0,
      documents: [],
      newDocumentsCount: 0,
      dashboard: null, // { quotesCount, quotes[], contracts[], milestones[], payouts[] }
      dashboardLoading: false,
      projects: [],
      marketLoading: false,
      marketEligible: true,
      // detail
      detailLoading: false,
      detail: null,
      // quote builder
      quote: null, // created DRAFT quote { id }
      laborItems: [this.emptyLine()],
      durationDays: '',
      validUntil: this.defaultValidUntil(),
      depositPct: '10',
      quoteSaving: false,
      quoteError: '',
      validationErrors: [],
      // ---- AI quote-drafting assistant (DRAFT ONLY) ----
      // Pre-fills the quote form below; the company edits, then the normal
      // createQuote -> submitQuote flow (with §7159) runs unchanged.
      aiDrafting: false,        // spinner while Claude drafts
      aiScopeDraft: '',         // editable drafted scope/description
      aiFollowUp: '',           // inline follow-up question from the model
      aiError: '',              // soft fallback message (never a dead-end)
      // my quotes
      quotesLoading: false,
      myQuotes: [],

      // ---- E4: two-sided commission breakdown + escrow payment mode ----
      // Commission rates are decimals (e.g. 0.10). Defaults match the api
      // (projectCommissions) so the breakdown still renders if the fetch is slow
      // or fails; the live numbers refresh once the queries resolve.
      clientRate: 0.10,
      proRate: 0.10,
      escrowThreshold: 1000, // USD; base < threshold => FULL (escrow-lite)
      escrowOverrides: {}, // { CATEGORY: 'FULL' | 'MILESTONE' | 'THRESHOLD' }

      // ---- messaging (step #3) ----
      // The company may message the (masked) client on a project it has
      // SUBMITTED/SELECTED a quote for. Server enforces the same gate.
      messaging: false,

      // ---- contract + e-signature (step #4) ----
      // Once the client has SELECTED this company's quote, the company reviews
      // and signs the prepared contract. Rendered by the shared ProjectContract
      // component, keyed off the SELECTED quote's id (prepareContract is idempotent).
      contractQuoteId: null, // non-null => render the contract view
    };
  }

  // eslint-disable-next-line class-methods-use-this
  emptyLine() {
    return { description: '', qty: '1', unitPrice: '' };
  }

  // eslint-disable-next-line class-methods-use-this
  defaultValidUntil() {
    // 14 days out (well within the 30-day server cap), YYYY-MM-DD for <input type=date>.
    const d = new Date();
    d.setDate(d.getDate() + 14);
    return d.toISOString().slice(0, 10);
  }

  componentDidMount() {
    this.bootstrap();
  }

  // eslint-disable-next-line class-methods-use-this
  firstError(res) {
    if (res && res.errors && res.errors.length) return res.errors[0].message;
    return 'Something went wrong.';
  }

  async bootstrap() {
    try {
      const res = await lpApi(Q_MY_COMPANY);
      if (res.errors) {
        const msg = this.firstError(res);
        const unauth = /401|unauth|log\s?in|token/i.test(msg);
        this.setState({ loading: false, authed: false, error: unauth ? '' : msg });
        return;
      }
      const cp = res.data && res.data.myCompanyProfile && res.data.myCompanyProfile.companyProfile;
      // Logged in but NOT a company yet -> send them to onboarding (CompanyAuth
      // shows the company landing; the wizard lives on /become-a-company).
      if (!cp) {
        this.setState({ loading: false, authed: true, eligibleCompany: false, company: null });
        return;
      }
      if (cp.kybStatus !== 'APPROVED') {
        this.setState({ loading: false, authed: true, eligibleCompany: false, company: cp });
        return;
      }
      this.setState({ loading: false, authed: true, eligibleCompany: true, company: cp });
      this.loadDashboard();
      this.loadMarketplace();
      this.loadMyQuotes();
      this.loadPricingConfig();
      this.loadNotifications();
      this.loadDocuments();
    } catch (e) {
      this.setState({ loading: false, error: `Could not load: ${e.message}` });
    }
  }

  onAuthenticated() {
    this.setState({ loading: true, authed: true, error: '' }, () => this.bootstrap());
  }

  async loadDashboard() {
    this.setState({ dashboardLoading: true });
    try {
      const res = await lpApi(Q_DASHBOARD);
      const env = res.data && res.data.companyDashboard;
      if (env && env.status === 200) {
        // Keep the FULL pipeline payload (not just a count) so the Dashboard
        // view can render quotes, contracts, milestones, and earnings. Client
        // identity is never exposed here — we only ever carry masked refs and
        // contract/project ids, never clientUserId.
        this.setState({
          dashboardLoading: false,
          dashboard: {
            quotesCount: (env.quotes || []).length,
            quotes: env.quotes || [],
            contracts: env.contracts || [],
            milestones: env.milestones || [],
            payouts: env.payouts || [],
          },
        });
      } else {
        this.setState({ dashboardLoading: false });
      }
    } catch (e) { this.setState({ dashboardLoading: false }); /* non-fatal: summary stays empty */ }
  }

  async loadMarketplace() {
    this.setState({ marketLoading: true, error: '' });
    try {
      const res = await lpApi(Q_MARKETPLACE, {});
      const env = res.data && res.data.marketplaceProjectRequests;
      if (res.errors || !env) {
        this.setState({ marketLoading: false, error: this.firstError(res) });
        return;
      }
      if (env.status && env.status >= 400) {
        this.setState({ marketLoading: false, error: env.errorMessage || `Error ${env.status}.` });
        return;
      }
      this.setState({
        marketLoading: false,
        projects: env.projectRequests || [],
        marketEligible: env.eligible !== false,
      });
    } catch (e) {
      this.setState({ marketLoading: false, error: `Could not load projects: ${e.message}` });
    }
  }

  async openProject(id) {
    this.setState({
      view: 'detail',
      detailLoading: true,
      detail: null,
      quote: null,
      quoteError: '',
      validationErrors: [],
      laborItems: [this.emptyLine()],
      durationDays: '',
      validUntil: this.defaultValidUntil(),
      depositPct: '10',
      messaging: false,
      aiDrafting: false,
      aiScopeDraft: '',
      aiFollowUp: '',
      aiError: '',
    });
    if (typeof window !== 'undefined') window.scrollTo(0, 0);
    try {
      const res = await lpApi(Q_PROJECT, { projectRequestId: String(id) });
      const env = res.data && res.data.projectRequest;
      if (res.errors || !env) {
        this.setState({ detailLoading: false, quoteError: this.firstError(res) });
        return;
      }
      if (env.status && env.status >= 400) {
        this.setState({ detailLoading: false, quoteError: env.errorMessage || `Error ${env.status}.` });
        return;
      }
      this.setState({ detailLoading: false, detail: env.projectRequest });
    } catch (e) {
      this.setState({ detailLoading: false, quoteError: `Could not load project: ${e.message}` });
    }
  }

  // Fetch the company's quotes into state (no view change) — used to gate
  // messaging in the project detail (a SUBMITTED/SELECTED quote unlocks chat).
  async loadMyQuotes() {
    try {
      const res = await lpApi(Q_MY_QUOTES);
      const env = res.data && res.data.myQuotes;
      this.setState({ myQuotes: (env && env.quotes) || [] });
    } catch (e) { /* non-fatal: gate just stays closed until a quote loads */ }
  }

  // E4 — load commission rates + escrow payment-mode config so the quote
  // builder can show the company exactly what the client pays, what they net,
  // and which escrow mode the job will use. Non-fatal: defaults already cover
  // the common 10%/10% + $1000-threshold case if either query fails.
  async loadPricingConfig() {
    try {
      const [cRes, eRes] = await Promise.all([lpApi(Q_COMMISSIONS), lpApi(Q_ESCROW)]);
      const c = cRes && cRes.data && cRes.data.projectCommissions;
      if (c && c.status === 200) {
        const cr = Number(c.projectCommissionClient);
        const pr = Number(c.projectCommissionPro);
        this.setState({
          clientRate: Number.isFinite(cr) ? cr : this.state.clientRate,
          proRate: Number.isFinite(pr) ? pr : this.state.proRate,
        });
      }
      const e = eRes && eRes.data && eRes.data.escrowSettings;
      if (e && e.status === 200) {
        const thr = Number(e.escrowThresholdUsd);
        let overrides = {};
        try { overrides = e.escrowCategoryOverridesJson ? JSON.parse(e.escrowCategoryOverridesJson) : {}; }
        catch (err) { overrides = {}; }
        this.setState({
          escrowThreshold: Number.isFinite(thr) ? thr : this.state.escrowThreshold,
          escrowOverrides: overrides && typeof overrides === 'object' ? overrides : {},
        });
      }
    } catch (e) { /* non-fatal: breakdown falls back to defaults */ }
  }

  // E4 — mirror besthand-api resolvePaymentMode for a live display hint:
  // per-category override wins (unless 'THRESHOLD'); else base vs threshold.
  // Fails safe to 'MILESTONE'. `base` is the company's entered subtotal.
  paymentModeFor(base) {
    const { escrowOverrides, escrowThreshold } = this.state;
    const cat = this.state.detail && this.state.detail.category;
    const override = cat && escrowOverrides ? escrowOverrides[String(cat).toUpperCase()] : undefined;
    if (override === 'FULL' || override === 'MILESTONE') return override;
    const amount = Number(base);
    const thr = Number(escrowThreshold);
    if (!Number.isFinite(amount) || !Number.isFinite(thr)) return 'MILESTONE';
    return amount < thr ? 'FULL' : 'MILESTONE';
  }

  async openMyQuotes() {
    this.setState({ view: 'quotes', quotesLoading: true });
    if (typeof window !== 'undefined') window.scrollTo(0, 0);
    try {
      const res = await lpApi(Q_MY_QUOTES);
      const env = res.data && res.data.myQuotes;
      this.setState({
        quotesLoading: false,
        myQuotes: (env && env.quotes) || [],
      });
    } catch (e) {
      this.setState({ quotesLoading: false, error: `Could not load quotes: ${e.message}` });
    }
  }

  // The company's quote on a given project, if any.
  myQuoteForProject(projectRequestId) {
    return (this.state.myQuotes || []).find(
      (q) => String(q.projectRequestId) === String(projectRequestId),
    ) || null;
  }

  // ---- quote line-item math ----
  setLine(idx, field, value) {
    this.setState((st) => {
      const lines = st.laborItems.slice();
      lines[idx] = { ...lines[idx], [field]: value };
      return { laborItems: lines };
    });
  }

  addLine() {
    this.setState((st) => ({ laborItems: [...st.laborItems, this.emptyLine()] }));
  }

  removeLine(idx) {
    this.setState((st) => {
      const lines = st.laborItems.filter((_, i) => i !== idx);
      return { laborItems: lines.length ? lines : [this.emptyLine()] };
    });
  }

  lineTotal(line) {
    const qty = parseFloat(line.qty);
    const unit = parseFloat(line.unitPrice);
    if (!Number.isFinite(qty) || !Number.isFinite(unit)) return 0;
    return Math.round(qty * unit * 100) / 100;
  }

  subtotal() {
    return this.state.laborItems.reduce((acc, l) => acc + this.lineTotal(l), 0);
  }

  // ---- AI: draft the quote, then PRE-FILL the existing form state ----
  // DRAFT ONLY. Calls aiQuoteDraft(projectRequestId) through the same lpApi
  // proxy as every other LP call. On success we map the returned scope / line
  // items / milestones into the EXISTING quote-form state (laborItems,
  // depositPct, aiScopeDraft) — we do NOT rebuild the form or auto-submit. The
  // company reviews/edits everything; the normal createQuote -> submitQuote
  // flow (with §7159) runs unchanged. 503/error => soft fallback, never a
  // dead-end (the company just fills it in manually).
  async draftWithAi() {
    const { detail } = this.state;
    if (!detail) return;
    this.setState({ aiDrafting: true, aiError: '', aiFollowUp: '' });
    try {
      const res = await lpApi(M_AI_QUOTE_DRAFT, { projectRequestId: String(detail.id) });
      const env = res.data && res.data.aiQuoteDraft;
      if (res.errors || !env) {
        this.setState({ aiDrafting: false, aiError: 'AI is unavailable right now — you can fill the quote in manually below.' });
        return;
      }
      if (env.status === 503) {
        this.setState({ aiDrafting: false, aiError: 'The AI assistant is not configured yet — fill the quote in manually below.' });
        return;
      }
      if (env.status && env.status >= 400) {
        this.setState({ aiDrafting: false, aiError: env.errorMessage || 'AI could not draft a quote — fill it in manually below.' });
        return;
      }

      // Parse the JSON-encoded draft arrays (defensive — soft fallback on bad JSON).
      let lineItems = [];
      let milestones = [];
      try { lineItems = env.lineItems ? JSON.parse(env.lineItems) : []; } catch (e) { lineItems = []; }
      try { milestones = env.milestones ? JSON.parse(env.milestones) : []; } catch (e) { milestones = []; }

      const patch = {
        aiDrafting: false,
        aiScopeDraft: env.scopeDescription || '',
        aiFollowUp: env.followUpQuestion || '',
        aiError: '',
      };

      // Map AI line items -> the EXISTING laborItems shape ({description, qty, unitPrice}).
      // Each drafted item is one labor/material line at qty 1 and unitPrice = amount.
      if (Array.isArray(lineItems) && lineItems.length > 0) {
        patch.laborItems = lineItems
          .filter((it) => it && Number.isFinite(Number(it.amount)) && Number(it.amount) > 0)
          .map((it) => ({
            description: String(it.label || (it.kind === 'MATERIAL' ? 'Materials' : 'Labor')).trim(),
            qty: '1',
            unitPrice: String(Number(it.amount)),
          }));
        if (patch.laborItems.length === 0) patch.laborItems = [this.emptyLine()];
      }

      // Derive the deposit % from the drafted milestones (down payment = first
      // milestone / total). The form turns depositPct back into milestonesProposal
      // at submit; §7159 is still the hard gate at submitQuote.
      if (Array.isArray(milestones) && milestones.length > 0) {
        const total = milestones.reduce((acc, m) => acc + (Number(m.amount) || 0), 0);
        const down = Number(milestones[0].amount) || 0;
        if (total > 0) {
          const pct = Math.round((down / total) * 100);
          patch.depositPct = String(Math.max(0, Math.min(100, pct)));
        }
      }

      this.setState(patch);
    } catch (e) {
      this.setState({ aiDrafting: false, aiError: `AI is unavailable (${e.message}) — fill the quote in manually below.` });
    }
  }

  // ---- submit the quote: createQuote -> updateQuote -> submitQuote ----
  async submitQuote() {
    const { detail, laborItems, durationDays, validUntil, depositPct } = this.state;
    if (!detail) return;
    const subtotal = this.subtotal();
    const lines = laborItems
      .map((l) => ({
        description: (l.description || '').trim(),
        qty: parseFloat(l.qty) || 0,
        unitPrice: parseFloat(l.unitPrice) || 0,
        lineTotal: this.lineTotal(l),
      }))
      .filter((l) => l.description && l.qty > 0 && l.unitPrice >= 0);

    if (lines.length === 0) {
      this.setState({ quoteError: 'Add at least one line item with a description, quantity, and unit price.' });
      return;
    }
    if (subtotal <= 0) {
      this.setState({ quoteError: 'The quote subtotal must be greater than zero.' });
      return;
    }
    const days = parseInt(durationDays, 10);
    if (!Number.isFinite(days) || days <= 0) {
      this.setState({ quoteError: 'Enter the estimated project duration in days.' });
      return;
    }

    this.setState({ quoteSaving: true, quoteError: '', validationErrors: [] });
    try {
      // 1) create (or reuse) the DRAFT quote for this project
      let quoteId = this.state.quote && this.state.quote.id;
      if (!quoteId) {
        const cr = await lpApi(M_CREATE_QUOTE, { projectRequestId: String(detail.id) });
        const env = cr.data && cr.data.createQuote;
        if (cr.errors || !env || (env.status && env.status >= 400)) {
          // 409 means a quote already exists — reuse the returned one if present.
          if (env && env.quote && env.quote.id) {
            quoteId = env.quote.id;
          } else {
            this.setState({ quoteSaving: false, quoteError: (env && env.errorMessage) || this.firstError(cr) });
            return;
          }
        } else {
          quoteId = env.quote.id;
        }
        this.setState({ quote: { id: quoteId } });
      }

      // 2) E2: milestones are denominated in the company BASE (= subtotal). The
      //    api validator sums them to quote.subtotal; the client is grossed up
      //    by the platform fee at charge time (client pays base*(1+clientRate)).
      const clientRate = this.state.clientRate != null ? this.state.clientRate : 0.10;
      const total = Math.round((subtotal * (1 + clientRate)) * 100) / 100; // client-facing, cap only
      // Down-payment is in BASE units. The jurisdiction cap is CLIENT-facing
      // (min($1000, 10%*total)), so the BASE deposit must satisfy
      // deposit*(1+clientRate) <= cap  =>  deposit <= cap/(1+clientRate).
      const pct = Math.max(0, Math.min(parseFloat(depositPct) || 0, 100)) / 100;
      let deposit = Math.round(subtotal * pct * 100) / 100;
      const clientCap = Math.min(1000, Math.round(total * 0.10 * 100) / 100);
      const baseCap = Math.round((clientCap / (1 + clientRate)) * 100) / 100;
      if (deposit > baseCap) deposit = baseCap;
      const remainder = Math.round((subtotal - deposit) * 100) / 100;
      const milestones = deposit > 0
        ? [
          { name: 'Deposit', amount: deposit },
          { name: 'Completion', amount: remainder },
        ]
        : [{ name: 'Completion', amount: subtotal }];

      const upd = await lpApi(M_UPDATE_QUOTE, {
        quoteId: String(quoteId),
        laborItemsJson: JSON.stringify(lines),
        materialsItemsJson: JSON.stringify([]),
        milestonesProposalJson: JSON.stringify(milestones),
        totalDurationDays: days,
        validUntil,
        subtotal,
      });
      const uErr = upd.data && upd.data.updateQuote;
      if (upd.errors || !uErr || (uErr.status && uErr.status >= 400)) {
        this.setState({ quoteSaving: false, quoteError: (uErr && uErr.errorMessage) || this.firstError(upd) });
        return;
      }

      // 3) submit
      const sub = await lpApi(M_SUBMIT_QUOTE, { quoteId: String(quoteId) });
      const sEnv = sub.data && sub.data.submitQuote;
      if (sub.errors || !sEnv || (sEnv.status && sEnv.status >= 400)) {
        this.setState({
          quoteSaving: false,
          quoteError: (sEnv && sEnv.errorMessage) || this.firstError(sub),
          validationErrors: (sEnv && sEnv.validationErrors) || [],
        });
        return;
      }
      this.setState({ quoteSaving: false });
      this.loadDashboard();
      this.openMyQuotes();
    } catch (e) {
      this.setState({ quoteSaving: false, quoteError: `Could not submit the quote: ${e.message}` });
    }
  }

  // ---- render ----
  render() {
    const { loading, authed, eligibleCompany } = this.state;

    if (!loading && !authed) {
      return <CompanyAuth onAuthenticated={() => this.onAuthenticated()} />;
    }

    return (
      <div className={s.page}>
        <section className={s.topbar}>
          <div className={s.wrap}>
            <div className={s.brandRow}>
              <span className={eligibleCompany && company ? s.greeting : s.eyebrow}>
                {eligibleCompany && company
                  ? `Hi! ${company.legalName || 'there'}`
                  : 'BestHand Large Projects · for companies'}
              </span>
              {eligibleCompany && (
                <div className={s.topActions}>
                  <nav className={s.tabs}>
                    <button type="button"
                      className={`${s.tab} ${this.state.view === 'dashboard' ? s.tabOn : ''}`}
                      onClick={() => { this.setState({ view: 'dashboard' }); this.loadDashboard(); }}>
                      Dashboard
                    </button>
                    <button type="button"
                      className={`${s.tab} ${(this.state.view === 'marketplace' || this.state.view === 'detail') ? s.tabOn : ''}`}
                      onClick={() => { this.setState({ view: 'marketplace' }); }}>
                      Project marketplace
                    </button>
                    <button type="button"
                      className={`${s.tab} ${this.state.view === 'quotes' ? s.tabOn : ''}`}
                      onClick={() => this.openMyQuotes()}>
                      My quotes
                    </button>
                    <a className={s.tab} href="/company/account" style={{ textDecoration: 'none' }}>
                      Account
                    </a>
                  </nav>
                  <button type="button" className={s.iconBtn} title="Notifications"
                    onClick={() => { this.setState({ view: 'notifications' }); this.markNotificationsRead(); }}>
                    <span className={s.iconGlyph}>🔔</span>
                    {this.state.unreadCount > 0 && (
                      <span className={s.badge}>{this.state.unreadCount > 99 ? '99+' : this.state.unreadCount}</span>
                    )}
                  </button>
                  <button type="button" className={s.iconBtn} title="Documents"
                    onClick={() => { this.setState({ view: 'documents' }); this.markDocumentsSeen(); }}>
                    <span className={s.iconGlyph}>✉️</span>
                    {this.state.newDocumentsCount > 0 && (
                      <span className={s.badge}>{this.state.newDocumentsCount > 99 ? '99+' : this.state.newDocumentsCount}</span>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </section>

        <section className={s.body}>
          <div className={s.wrap}>
            {loading ? (
              <div className={s.loading}>Loading…</div>
            ) : this.renderContent()}
          </div>
        </section>
      </div>
    );
  }

  // ---- Bell + mail data --------------------------------------------------
  async loadNotifications() {
    try {
      const res = await lpApi(Q_NOTIFICATIONS);
      const env = res.data && res.data.companyNotifications;
      if (env && env.status === 200) {
        this.setState({
          notifications: env.companyNotifications || [],
          unreadCount: env.unreadCount || 0,
        });
      }
    } catch (e) { /* best-effort */ }
  }

  async markNotificationsRead() {
    try {
      await lpApi(M_MARK_NOTIF_READ);
      this.setState(st => ({
        unreadCount: 0,
        notifications: (st.notifications || []).map(n => ({ ...n, isRead: true })),
      }));
    } catch (e) { /* best-effort */ }
  }

  async loadDocuments() {
    try {
      const res = await lpApi(Q_DOCUMENTS);
      const env = res.data && res.data.companyDocuments;
      if (env && env.status === 200) {
        this.setState({
          documents: env.companyDocuments || [],
          newDocumentsCount: env.newDocumentsCount || 0,
        });
      }
    } catch (e) { /* best-effort */ }
  }

  async markDocumentsSeen() {
    try {
      await lpApi(M_MARK_DOCS_SEEN);
      this.setState({ newDocumentsCount: 0 });
    } catch (e) { /* best-effort */ }
  }

  renderNotifications() {
    const items = this.state.notifications || [];
    return (
      <div>
        <h2 className={s.sectionTitle}>Notifications</h2>
        {items.length === 0 ? (
          <div className={s.loading}>No notifications yet.</div>
        ) : items.map(n => (
          <div key={n.id} className={s.feedRow} style={!n.isRead ? { background: '#FFF6F4', borderColor: '#F1D3CB' } : {}}>
            <div className={s.feedMain}>
              <div className={s.feedTitle}>{n.title}</div>
              {n.body ? <div className={s.feedSub}>{n.body}</div> : null}
            </div>
            {!n.isRead ? <span className={s.dot} /> : null}
          </div>
        ))}
      </div>
    );
  }

  renderDocuments() {
    const items = this.state.documents || [];
    return (
      <div>
        <h2 className={s.sectionTitle}>Documents</h2>
        {items.length === 0 ? (
          <div className={s.loading}>No documents yet. Contracts, lien waivers and payout receipts will appear here.</div>
        ) : items.map((d, i) => (
          <a key={i} className={s.feedRow} href={d.url || undefined} target={d.url ? '_blank' : undefined} rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'inherit', cursor: d.url ? 'pointer' : 'default' }}>
            <div className={s.feedMain}>
              <div className={s.feedTitle}>
                {d.title}
                {d.isNew ? <span className={s.newTag}>New</span> : null}
              </div>
              {d.subtitle ? <div className={s.feedSub}>{d.subtitle}</div> : null}
            </div>
            {d.url ? <span className={s.openHint}>Open ›</span> : null}
          </a>
        ))}
      </div>
    );
  }

  renderContent() {
    if (!this.state.eligibleCompany) return this.renderNotApproved();
    if (this.state.contractQuoteId) return this.renderContract();
    if (this.state.view === 'detail') return this.renderDetail();
    if (this.state.view === 'quotes') return this.renderQuotes();
    if (this.state.view === 'notifications') return this.renderNotifications();
    if (this.state.view === 'documents') return this.renderDocuments();
    if (this.state.view === 'dashboard') return this.renderDashboard();
    return this.renderMarketplace();
  }

  // Step #4: company-side contract review + e-signature for a SELECTED quote.
  renderContract() {
    const quoteId = this.state.contractQuoteId;
    const q = (this.state.myQuotes || []).find((x) => String(x.id) === String(quoteId));
    return (
      <ProjectContract
        viewerRole="COMPANY"
        projectQuoteId={quoteId}
        projectTitle={q ? (q.anonymousRef || `Quote #${q.id}`) : 'Selected project'}
        backLabel="Back to my quotes"
        onBack={() => this.setState({ contractQuoteId: null })}
      />
    );
  }

  renderNotApproved() {
    const { company } = this.state;
    const status = company && company.kybStatus;
    return (
      <div className={s.gate}>
        <h1 className={s.gateTitle}>
          {company ? 'Your company isn’t approved yet' : 'Set up your company first'}
        </h1>
        <p className={s.gateBody}>
          {company
            ? `Your company verification is currently ${status || 'pending'}. Once an admin approves your KYB, matched projects will appear here.`
            : 'Create and verify your company to start receiving large project requests in your service area.'}
        </p>
        <a className={s.btn} href="/become-a-company">
          {company ? 'View onboarding status' : 'Become a company'}
        </a>
      </div>
    );
  }

  renderSummary() {
    const { company, dashboard, projects } = this.state;
    return (
      <div className={s.summaryRow}>
        <div className={s.summaryCard}>
          <span className={s.summaryNum}>{projects.length}</span>
          <span className={s.summaryLabel}>Open projects in your area</span>
        </div>
        <div className={s.summaryCard}>
          <span className={s.summaryNum}>{dashboard ? dashboard.quotesCount : '—'}</span>
          <span className={s.summaryLabel}>Quotes submitted</span>
        </div>
        <div className={s.summaryCardWide}>
          <span className={s.summaryLabel}>Signed in as</span>
          <span className={s.summaryCompany}>{(company && company.legalName) || 'Your company'}</span>
        </div>
      </div>
    );
  }

  // ---- DASHBOARD (lightweight CRM) ----------------------------------------
  // Renders the full companyDashboard payload: KPI cards, the quotes pipeline,
  // won contracts, per-contract milestones (escrow state), and net earnings.
  // Everything client-facing is masked (anonymous refs / project ids only).
  renderDashboard() {
    const { dashboard, dashboardLoading, company } = this.state;
    if (dashboardLoading && !dashboard) return <div className={s.loading}>Loading dashboard…</div>;

    const quotes = (dashboard && dashboard.quotes) || [];
    const contracts = (dashboard && dashboard.contracts) || [];
    const milestones = (dashboard && dashboard.milestones) || [];
    const payouts = (dashboard && dashboard.payouts) || [];

    // ---- KPI math ----
    const ACTIVE_CONTRACT = ['IN_CANCELLATION_PERIOD', 'ACTIVE'];
    const activeContracts = contracts.filter((c) => ACTIVE_CONTRACT.includes(c.status));
    const wonContracts = contracts.length; // every contract = a won project
    const quotesSubmitted = quotes.filter((q) => q.status && q.status !== 'DRAFT').length;
    const quotesSelected = quotes.filter((q) => q.status === 'SELECTED').length;
    const quotesPending = quotes.filter((q) => q.status === 'SUBMITTED').length;
    const milestonesInProgress = milestones.filter(
      (m) => ['IN_PROGRESS', 'SUBMITTED_FOR_REVIEW'].includes(m.status),
    ).length;

    // Earnings: transferred payouts = what the company actually received (net of
    // commission). `amount` on a payout is already the net transfer to the
    // company's connected account; reversedAmount claws back. Pending = payouts
    // not yet transferred.
    const netOf = (p) => Math.max(0, (Number(p.amount) || 0) - (Number(p.reversedAmount) || 0));
    const isTransferred = (p) => p.status === 'TRANSFERRED' || !!p.transferredAt;
    const totalEarned = payouts.filter(isTransferred).reduce((acc, p) => acc + netOf(p), 0);
    const pendingPayouts = payouts.filter((p) => !isTransferred(p) && p.status !== 'REVERSED')
      .reduce((acc, p) => acc + netOf(p), 0);

    if (!dashboard || (quotes.length === 0 && contracts.length === 0 && payouts.length === 0)) {
      return (
        <div>
          {this.renderDashHead(company)}
          <div className={s.empty}>
            <EmptyMascot className={s.emptyImg} />
            <h3 className={s.emptyTitle}>Your pipeline is empty</h3>
            <p className={s.emptyBody}>
              Quotes you submit, projects you win, and payouts you earn will all show up
              here. Browse the marketplace to land your first large project.
            </p>
            <button type="button" className={s.btn} style={{ marginTop: 18 }}
              onClick={() => this.setState({ view: 'marketplace' })}>
              Browse the marketplace
            </button>
          </div>
        </div>
      );
    }

    return (
      <div>
        {this.renderDashHead(company)}

        {/* KPI cards */}
        <div className={s.kpiRow}>
          {this.renderKpi(ICONS.briefcase, wonContracts, 'Active projects',
            activeContracts.length ? `${activeContracts.length} in progress` : 'Won & contracted')}
          {this.renderKpi(ICONS.doc, quotesSubmitted, 'Quotes submitted',
            `${quotesSelected} selected · ${quotesPending} pending`)}
          {this.renderKpi(ICONS.layers, milestonesInProgress, 'Milestones in progress',
            `${milestones.length} total`)}
          {this.renderKpiMoney(ICONS.dollar, pendingPayouts, totalEarned)}
        </div>

        {this.renderDashQuotes(quotes)}
        {this.renderDashContracts(contracts)}
        {this.renderDashMilestones(contracts, milestones)}
        {this.renderDashPayouts(payouts, totalEarned)}
      </div>
    );
  }

  // eslint-disable-next-line class-methods-use-this
  renderDashHead(company) {
    return (
      <div className={s.headRow}>
        <div>
          <h1 className={s.h1}>Dashboard</h1>
          <p className={s.sub}>
            Your pipeline at a glance — quotes, won projects, escrow milestones, and
            earnings for {(company && company.legalName) || 'your company'}. Clients stay
            anonymous; you see a project reference, never their contact details.
          </p>
        </div>
        <button type="button" className={s.btnGhost} onClick={() => this.setState({ view: 'marketplace' })}>
          Browse projects
        </button>
      </div>
    );
  }

  // eslint-disable-next-line class-methods-use-this
  renderKpi(icon, value, label, hint) {
    return (
      <div className={s.kpiCard}>
        <span className={s.kpiIcon}><Icon d={icon} /></span>
        <span className={s.kpiNum}>{value == null ? '—' : value}</span>
        <span className={s.kpiLabel}>{label}</span>
        {hint ? <span className={s.kpiHint}>{hint}</span> : null}
      </div>
    );
  }

  // eslint-disable-next-line class-methods-use-this
  renderKpiMoney(icon, pending, earned) {
    return (
      <div className={`${s.kpiCard} ${s.kpiCardMoney}`}>
        <span className={s.kpiIcon}><Icon d={icon} /></span>
        <span className={s.kpiNum}>{money(earned)}</span>
        <span className={s.kpiLabel}>Total earned</span>
        <span className={s.kpiHint}>{money(pending)} pending payout</span>
      </div>
    );
  }

  // My Quotes pipeline — status chips, newest first.
  // eslint-disable-next-line class-methods-use-this
  renderDashQuotes(quotes) {
    if (!quotes.length) return null;
    return (
      <section className={s.dashBlock}>
        <div className={s.dashBlockHead}>
          <h2 className={s.dashTitle}>My quotes</h2>
          <span className={s.dashCount}>{quotes.length}</span>
        </div>
        <div className={s.dashList}>
          {quotes.map((q) => (
            <div className={s.dashItem} key={q.id}>
              <div className={s.dashItemMain}>
                <span className={s.dashItemTitle}>{q.anonymousRef || `Quote #${q.id}`}</span>
                <span className={s.dashItemSub}>
                  Project #{q.projectRequestId}
                  {q.totalDurationDays ? ` · ${q.totalDurationDays} days` : ''}
                  {shortDate(q.submittedAt || q.createdAt) ? ` · ${shortDate(q.submittedAt || q.createdAt)}` : ''}
                </span>
              </div>
              <span className={s.dashItemAmount}>{money(q.total != null ? q.total : q.subtotal)}</span>
              <span className={`${s.statusPill} ${s[`st_${statusKey(q.status || 'DRAFT')}`] || ''}`}>
                {q.status || 'DRAFT'}
              </span>
            </div>
          ))}
        </div>
      </section>
    );
  }

  renderDashContracts(contracts) {
    if (!contracts.length) return null;
    return (
      <section className={s.dashBlock}>
        <div className={s.dashBlockHead}>
          <h2 className={s.dashTitle}>My contracts</h2>
          <span className={s.dashCount}>{contracts.length}</span>
        </div>
        <div className={s.dashList}>
          {contracts.map((c) => {
            const deadline = c.status === 'IN_CANCELLATION_PERIOD' ? shortDate(c.cancellationDeadline) : null;
            return (
              <div className={s.dashItem} key={c.id}>
                <div className={s.dashItemMain}>
                  <span className={s.dashItemTitle}>{maskedContractRef(c)}</span>
                  <span className={s.dashItemSub}>
                    Contract #{c.id}
                    {shortDate(c.createdAt) ? ` · started ${shortDate(c.createdAt)}` : ''}
                    {deadline ? ` · cancellable until ${deadline}` : ''}
                    {c.completedAt ? ` · completed ${shortDate(c.completedAt)}` : ''}
                  </span>
                </div>
                <span className={s.dashItemAmount}>{money(c.totalAmount)}</span>
                <span className={`${s.statusPill} ${s[`st_${statusKey(c.status)}`] || ''}`}>
                  {statusLabel(c.status)}
                </span>
              </div>
            );
          })}
        </div>
      </section>
    );
  }

  // Milestones grouped per ACTIVE contract — escrow / payout state per line.
  renderDashMilestones(contracts, milestones) {
    if (!milestones.length) return null;
    const ACTIVE = ['IN_CANCELLATION_PERIOD', 'ACTIVE'];
    const activeContracts = contracts.filter((c) => ACTIVE.includes(c.status));
    // Show milestones for active contracts first; fall back to all if none active.
    const shown = activeContracts.length ? activeContracts : contracts;
    const byContract = shown
      .map((c) => ({
        contract: c,
        items: milestones
          .filter((m) => String(m.projectContractId) === String(c.id))
          .sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0)),
      }))
      .filter((g) => g.items.length > 0);
    if (!byContract.length) return null;
    return (
      <section className={s.dashBlock}>
        <div className={s.dashBlockHead}>
          <h2 className={s.dashTitle}>My milestones</h2>
          <span className={s.dashCount}>{milestones.length}</span>
        </div>
        {byContract.map((g) => (
          <div className={s.msGroup} key={g.contract.id}>
            <div className={s.msGroupHead}>
              <span className={s.msGroupTitle}>{maskedContractRef(g.contract)}</span>
              <span className={s.msGroupSub}>Contract #{g.contract.id} · {money(g.contract.totalAmount)}</span>
            </div>
            <div className={s.dashList}>
              {g.items.map((m) => (
                <div className={s.msRow} key={m.id}>
                  <div className={s.dashItemMain}>
                    <span className={s.dashItemTitle}>
                      {m.name || `Milestone ${m.orderIndex != null ? m.orderIndex + 1 : ''}`}
                      {m.isDownPayment ? <span className={s.msTag}>Deposit</span> : null}
                    </span>
                    <span className={s.dashItemSub}>
                      {m.status === 'PAID' && m.paidAt ? `Released ${shortDate(m.paidAt)}`
                        : m.status === 'SUBMITTED_FOR_REVIEW' ? 'Awaiting client approval'
                          : m.status === 'IN_PROGRESS' ? 'In escrow — work in progress'
                            : m.status === 'READY' ? 'Ready to start' : statusLabel(m.status)}
                    </span>
                  </div>
                  <span className={s.dashItemAmount}>{money(m.amount)}</span>
                  <span className={`${s.statusPill} ${s[`st_${statusKey(m.status)}`] || ''}`}>
                    {statusLabel(m.status)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </section>
    );
  }

  // Payouts / earnings — what the company actually received, net of commission.
  // eslint-disable-next-line class-methods-use-this
  renderDashPayouts(payouts, totalEarned) {
    if (!payouts.length) return null;
    return (
      <section className={s.dashBlock}>
        <div className={s.dashBlockHead}>
          <h2 className={s.dashTitle}>Payouts &amp; earnings</h2>
          <span className={s.dashTotalBadge}>
            <Icon d={ICONS.trend} /> {money(totalEarned)} earned
          </span>
        </div>
        <div className={s.dashList}>
          {payouts.map((p) => {
            const net = Math.max(0, (Number(p.amount) || 0) - (Number(p.reversedAmount) || 0));
            return (
              <div className={s.dashItem} key={p.id}>
                <div className={s.dashItemMain}>
                  <span className={s.dashItemTitle}>Payout #{p.id}</span>
                  <span className={s.dashItemSub}>
                    Contract #{p.projectContractId}
                    {p.transferredAt ? ` · transferred ${shortDate(p.transferredAt)}`
                      : shortDate(p.createdAt) ? ` · created ${shortDate(p.createdAt)}` : ''}
                    {p.reversedAmount ? ` · ${money(p.reversedAmount)} reversed` : ''}
                  </span>
                </div>
                <span className={s.dashItemAmount}>{money(net)}</span>
                <span className={`${s.statusPill} ${s[`st_${statusKey(p.status)}`] || ''}`}>
                  {statusLabel(p.status)}
                </span>
              </div>
            );
          })}
        </div>
      </section>
    );
  }

  renderMarketplace() {
    const { projects, marketLoading, marketEligible } = this.state;
    return (
      <div>
        <div className={s.headRow}>
          <div>
            <h1 className={s.h1}>Project marketplace</h1>
            <p className={s.sub}>
              Published projects in your eligible states. Client identity and exact address
              stay private until you’re selected — you see city, state, and ZIP.
            </p>
          </div>
        </div>

        {this.renderSummary()}

        {this.state.error ? <div className={s.alertErr}>{this.state.error}</div> : null}
        {!marketEligible && (
          <div className={s.alertWarn}>
            Your company isn’t currently eligible to quote in any state with open projects.
            Check your licenses, insurance, and that your states are enabled.
          </div>
        )}

        {marketLoading ? (
          <div className={s.loading}>Loading projects…</div>
        ) : projects.length === 0 ? (
          this.renderEmpty()
        ) : (
          <div className={s.grid}>
            {projects.map((p) => this.renderCard(p))}
          </div>
        )}
      </div>
    );
  }

  renderEmpty() {
    return (
      <div className={s.empty}>
        <EmptyMascot className={s.emptyImg} />
        <h3 className={s.emptyTitle}>No open projects right now</h3>
        <p className={s.emptyBody}>
          New projects in your eligible states will show up here. Check back soon —
          we’ll match you to big jobs as clients post them.
        </p>
      </div>
    );
  }

  renderCard(p) {
    const photos = (p.mediaAssets || []).filter((m) => m.kind === 'PHOTO').length;
    const timeline = safeParse(p.timelineJson);
    const when = timeline && (timeline.desiredStart || timeline.start);
    return (
      <button type="button" key={p.id} className={s.card} onClick={() => this.openProject(p.id)}>
        <div className={s.cardTop}>
          <span className={s.catPill}>{CATEGORY_LABEL[p.category] || p.category}</span>
          {p.completenessScore != null && (
            <span className={s.scorePill}>
              <Icon d={ICONS.gauge} /> {p.completenessScore}% complete
            </span>
          )}
        </div>
        <h3 className={s.cardTitle}>{p.title || `${CATEGORY_LABEL[p.category] || p.category} project`}</h3>
        <div className={s.cardMetaRow}>
          <span className={s.cardMeta}><Icon d={ICONS.pin} /> {p.siteCity}, {p.siteState} {p.siteZip}</span>
          <span className={s.cardMeta}><Icon d={ICONS.wallet} /> {budgetRange(p.budgetMin, p.budgetMax)}</span>
        </div>
        <div className={s.cardMetaRow}>
          {when && <span className={s.cardMeta}><Icon d={ICONS.clock} /> {when}</span>}
          <span className={s.cardMeta}><Icon d={ICONS.photo} /> {photos} guided photo{photos === 1 ? '' : 's'}</span>
        </div>
        <div className={s.cardFoot}>
          <span className={s.cardRef}>{p.anonymousClientRef || 'New client'}</span>
          <span className={s.cardCta}>View &amp; quote →</span>
        </div>
      </button>
    );
  }

  renderDetail() {
    const { detail, detailLoading } = this.state;
    if (detailLoading) return <div className={s.loading}>Loading project…</div>;
    if (!detail) {
      return (
        <div>
          <button type="button" className={s.backBtn} onClick={() => this.setState({ view: 'marketplace' })}>
            <Icon d={ICONS.back} /> Back to marketplace
          </button>
          {this.state.quoteError ? <div className={s.alertErr}>{this.state.quoteError}</div> : null}
        </div>
      );
    }
    const measurements = safeParse(detail.measurementsJson);
    const intake = safeParse(detail.intakeAnswersJson);
    const timeline = safeParse(detail.timelineJson);
    const photos = (detail.mediaAssets || []).filter((m) => m.kind === 'PHOTO');

    // Messaging is unlocked once this company has SUBMITTED (or had SELECTED) a
    // quote for this project. companyId = our own profile id; the client stays
    // masked behind anonymousClientRef.
    const myQuote = this.myQuoteForProject(detail.id);
    const canMessage = !!(this.state.company && this.state.company.id && myQuote
      && ['SUBMITTED', 'SELECTED'].includes(myQuote.status));

    if (this.state.messaging && canMessage) {
      return (
        <div>
          <button type="button" className={s.backBtn}
            onClick={() => this.setState({ messaging: false })}>
            <Icon d={ICONS.back} /> Back to project
          </button>
          <ProjectThread
            viewerRole="COMPANY"
            projectRequestId={detail.id}
            companyId={this.state.company.id}
            counterparty={detail.anonymousClientRef || 'Homeowner'}
            projectTitle={detail.title || 'Project request'}
          />
        </div>
      );
    }

    return (
      <div>
        <button type="button" className={s.backBtn} onClick={() => this.setState({ view: 'marketplace' })}>
          <Icon d={ICONS.back} /> Back to marketplace
        </button>

        <div className={s.detailHead}>
          <span className={s.catPill}>{CATEGORY_LABEL[detail.category] || detail.category}</span>
          <h1 className={s.h1}>{detail.title || 'Project request'}</h1>
          <div className={s.cardMetaRow}>
            <span className={s.cardMeta}><Icon d={ICONS.pin} /> {detail.siteCity}, {detail.siteState} {detail.siteZip}</span>
            <span className={s.cardMeta}><Icon d={ICONS.wallet} /> {budgetRange(detail.budgetMin, detail.budgetMax)}</span>
            <span className={s.cardMeta}>{detail.anonymousClientRef || 'New client'}</span>
          </div>
          <p className={s.privacyNote}>
            Exact street address and client contact details are hidden until a quote is selected.
          </p>
          {canMessage && (
            <button type="button" className={s.btn} style={{ marginTop: 12 }}
              onClick={() => this.setState({ messaging: true })}>
              Message homeowner
            </button>
          )}
        </div>

        <div className={s.detailGrid}>
          <div className={s.detailMain}>
            {detail.description && (
              <div className={s.block}>
                <h3 className={s.blockTitle}>Scope</h3>
                <p className={s.blockText}>{detail.description}</p>
              </div>
            )}

            {photos.length > 0 && (
              <div className={s.block}>
                <h3 className={s.blockTitle}>Guided photos ({photos.length})</h3>
                <div className={s.photoGrid}>
                  {photos.map((m) => {
                    const src = m.cdnUrl || m.thumbnailUrl;
                    return (
                      <div className={s.photoCell} key={m.id}>
                        {src ? (
                          <img src={src} alt={m.slot || 'Project photo'}
                            onError={(e) => { e.target.style.display = 'none'; }} />
                        ) : (
                          <div className={s.photoPlaceholder}><Icon d={ICONS.photo} /></div>
                        )}
                        {m.slot ? <span className={s.photoSlot}>{m.slot}</span> : null}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {measurements && Object.keys(measurements).length > 0 && (
              <div className={s.block}>
                <h3 className={s.blockTitle}>Measurements</h3>
                {this.renderKeyVals(measurements)}
              </div>
            )}

            {intake && Object.keys(intake).length > 0 && (
              <div className={s.block}>
                <h3 className={s.blockTitle}>Project details</h3>
                {this.renderKeyVals(intake)}
              </div>
            )}
          </div>

          <aside className={s.detailSide}>
            <div className={s.factCard}>
              <h3 className={s.blockTitle}>At a glance</h3>
              <dl className={s.dl}>
                <dt>Budget</dt><dd>{budgetRange(detail.budgetMin, detail.budgetMax)}{detail.budgetIsFirm ? ' (firm)' : ''}</dd>
                {detail.ambitionLevel && (<><dt>Quality level</dt><dd>{AMBITION_LABEL[detail.ambitionLevel] || detail.ambitionLevel}</dd></>)}
                {detail.propertyType && (<><dt>Property</dt><dd>{detail.propertyType.replace(/_/g, ' ').toLowerCase()}</dd></>)}
                {detail.propertyYearBuilt && (<><dt>Year built</dt><dd>{detail.propertyYearBuilt}</dd></>)}
                {timeline && (timeline.desiredStart || timeline.start) && (<><dt>Timeline</dt><dd>{timeline.desiredStart || timeline.start}</dd></>)}
                {detail.hasPermit && (<><dt>Permit</dt><dd>{String(detail.hasPermit).replace(/_/g, ' ').toLowerCase()}</dd></>)}
                {detail.completenessScore != null && (<><dt>Completeness</dt><dd>{detail.completenessScore}%</dd></>)}
              </dl>
            </div>
          </aside>
        </div>

        {this.renderQuoteForm()}
      </div>
    );
  }

  // eslint-disable-next-line class-methods-use-this
  renderKeyVals(obj) {
    return (
      <dl className={s.dl}>
        {Object.keys(obj).map((k) => {
          const v = obj[k];
          const label = k.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim();
          const val = Array.isArray(v) ? v.join(', ') : (v == null ? '—' : String(v));
          return (
            <React.Fragment key={k}>
              <dt>{label.charAt(0).toUpperCase() + label.slice(1)}</dt>
              <dd>{val}</dd>
            </React.Fragment>
          );
        })}
      </dl>
    );
  }

  renderQuoteForm() {
    const { laborItems, durationDays, validUntil, depositPct, quoteSaving, quoteError, validationErrors,
      clientRate, proRate, aiDrafting, aiScopeDraft, aiFollowUp, aiError } = this.state;
    const subtotal = this.subtotal(); // the company BASE
    // Client-side fee is added on top of the base; the company's payout has the
    // pro-side commission deducted from the base. Both rates come from the api
    // (projectCommissions); defaults 10%/10% until they load.
    const commission = Math.round(subtotal * clientRate * 100) / 100;
    const total = Math.round((subtotal + commission) * 100) / 100; // what the client pays
    const youReceive = Math.round(subtotal * (1 - proRate) * 100) / 100; // company payout
    const mode = this.paymentModeFor(subtotal);
    const modeIsFull = mode === 'FULL';
    return (
      <div className={s.quoteCard} id="quote">
        <h2 className={s.quoteTitle}>Submit your quote</h2>
        <p className={s.sub}>
          Build a line-item quote at your <strong>base price</strong>. A {Math.round(clientRate * 100)}%
          platform service fee is added on top for the client, and a {Math.round(proRate * 100)}% platform
          commission is deducted from your payout. Payments are held in escrow and released as the job completes.
        </p>

        {/* AI quote-drafting assistant (DRAFT ONLY). Pre-fills the form below;
            the company edits everything before the normal submit flow runs. */}
        <div className={s.aiBox}>
          <div className={s.aiHead}>
            <div>
              <span className={s.aiTitle}>Draft with AI</span>
              <span className={s.aiHint}>
                Get a starting scope, line items, and a fair (non-front-loaded) milestone deposit — then edit before you submit.
              </span>
            </div>
            <button type="button" className={s.btnGhost} disabled={aiDrafting}
              onClick={() => this.draftWithAi()}>
              {aiDrafting ? 'Drafting…' : 'Draft with AI'}
            </button>
          </div>
          {aiError ? <div className={s.aiFallback}>{aiError}</div> : null}
          {aiFollowUp ? <div className={s.aiFollow}>{aiFollowUp}</div> : null}
          {aiScopeDraft ? (
            <label className={s.field} style={{ marginTop: 10 }}>
              <span>Drafted scope (edit freely)</span>
              <textarea className={s.input} rows={4} value={aiScopeDraft}
                onChange={(e) => this.setState({ aiScopeDraft: e.target.value })} />
            </label>
          ) : null}
        </div>

        {quoteError ? <div className={s.alertErr}>{quoteError}</div> : null}
        {validationErrors && validationErrors.length > 0 && (
          <div className={s.alertErr}>
            Quote validation failed: {validationErrors.join(', ')}
          </div>
        )}

        <div className={s.lineHead}>
          <span className={s.lhDesc}>Description</span>
          <span className={s.lhQty}>Qty</span>
          <span className={s.lhUnit}>Unit price</span>
          <span className={s.lhTotal}>Line total</span>
          <span className={s.lhDel} />
        </div>

        {laborItems.map((line, idx) => (
          // eslint-disable-next-line react/no-array-index-key
          <div className={s.lineRow} key={idx}>
            <input className={`${s.input} ${s.lhDesc}`} value={line.description}
              placeholder="e.g. Demo &amp; haul-away, tile install…"
              onChange={(e) => this.setLine(idx, 'description', e.target.value)} />
            <input className={`${s.input} ${s.lhQty}`} type="number" min="0" step="1" value={line.qty}
              onChange={(e) => this.setLine(idx, 'qty', e.target.value)} />
            <input className={`${s.input} ${s.lhUnit}`} type="number" min="0" step="0.01" value={line.unitPrice}
              placeholder="0.00"
              onChange={(e) => this.setLine(idx, 'unitPrice', e.target.value)} />
            <span className={`${s.lineTotalCell} ${s.lhTotal}`}>{money(this.lineTotal(line))}</span>
            <button type="button" className={s.lineDel} onClick={() => this.removeLine(idx)} aria-label="Remove line">×</button>
          </div>
        ))}

        <button type="button" className={s.addLine} onClick={() => this.addLine()}>+ Add line item</button>

        <div className={s.quoteMetaGrid}>
          <label className={s.field}>
            <span>Estimated duration (days) *</span>
            <input className={s.input} type="number" min="1" step="1" value={durationDays}
              onChange={(e) => this.setState({ durationDays: e.target.value })} placeholder="14" />
          </label>
          <label className={s.field}>
            <span>Quote valid until *</span>
            <input className={s.input} type="date" value={validUntil}
              onChange={(e) => this.setState({ validUntil: e.target.value })} />
          </label>
          <label className={s.field}>
            <span>Requested deposit (%)</span>
            <input className={s.input} type="number" min="0" max="100" step="1" value={depositPct}
              onChange={(e) => this.setState({ depositPct: e.target.value })} />
          </label>
        </div>

        {/* E4 — two-sided breakdown: what the client pays vs what the company
            nets, computed live from the entered base + fetched commission rates,
            plus the escrow payment-mode badge. */}
        <div className={s.breakdown}>
          <div className={s.bkHead}>
            <span className={s.bkTitle}>Payment breakdown</span>
            <span className={`${s.modeBadge} ${modeIsFull ? s.modeFull : s.modeMilestone}`}>
              <Icon d={ICONS.shield} />
              {modeIsFull ? 'Paid in full (escrow-lite)' : 'Milestone escrow'}
            </span>
          </div>
          <div className={s.bkRow}>
            <div className={s.bkLabel}>
              <span className={s.bkName}>Your price (base)</span>
              <span className={s.bkHint}>What your quote is built on</span>
            </div>
            <span className={s.bkVal}>{money(subtotal)}</span>
          </div>
          <div className={s.bkRow}>
            <div className={s.bkLabel}>
              <span className={s.bkName}>Client pays</span>
              <span className={s.bkHint}>incl. {Math.round(clientRate * 100)}% platform service fee</span>
            </div>
            <span className={`${s.bkVal} ${s.bkValClient}`}>{money(total)}</span>
          </div>
          <div className={`${s.bkRow} ${s.bkRowNet}`}>
            <div className={s.bkLabel}>
              <span className={s.bkName}>You receive</span>
              <span className={s.bkHint}>after {Math.round(proRate * 100)}% platform commission</span>
            </div>
            <span className={`${s.bkVal} ${s.bkValNet}`}>{money(youReceive)}</span>
          </div>
          <p className={s.bkNote}>
            {modeIsFull
              ? 'This job is paid in full up front, held in escrow, and released to you on completion.'
              : 'This job is paid across milestones, each released from escrow as the work is completed.'}
          </p>
        </div>

        <div className={s.quoteActions}>
          <button type="button" className={s.btn} disabled={quoteSaving} onClick={() => this.submitQuote()}>
            {quoteSaving ? 'Submitting…' : 'Submit quote'}
          </button>
        </div>
      </div>
    );
  }

  renderQuotes() {
    const { myQuotes, quotesLoading } = this.state;
    return (
      <div>
        <div className={s.headRow}>
          <div>
            <h1 className={s.h1}>My quotes</h1>
            <p className={s.sub}>Every quote your company has submitted, with its current status.</p>
          </div>
          <button type="button" className={s.btnGhost} onClick={() => { this.setState({ view: 'marketplace' }); }}>
            Browse projects
          </button>
        </div>

        {quotesLoading ? (
          <div className={s.loading}>Loading quotes…</div>
        ) : myQuotes.length === 0 ? (
          <div className={s.empty}>
            <EmptyMascot className={s.emptyImg} />
            <h3 className={s.emptyTitle}>No quotes yet</h3>
            <p className={s.emptyBody}>Quotes you submit on the marketplace will appear here.</p>
          </div>
        ) : (
          <div className={s.quoteList}>
            {myQuotes.map((q) => (
              <div className={s.quoteRow} key={q.id}>
                <div className={s.quoteRowMain}>
                  <span className={s.quoteRef}>{q.anonymousRef || `Quote #${q.id}`}</span>
                  <span className={s.quoteSubtitle}>Project #{q.projectRequestId}</span>
                </div>
                <div className={s.quoteRowMeta}>
                  <span className={s.quoteAmount}>{money(q.total)}</span>
                  {q.totalDurationDays ? <span className={s.quoteDays}>{q.totalDurationDays} days</span> : null}
                </div>
                <span className={`${s.statusPill} ${s[`st_${(q.status || 'DRAFT').toLowerCase()}`] || ''}`}>
                  {q.status || 'DRAFT'}
                </span>
                {q.status === 'SELECTED' && (
                  <button type="button" className={s.btn} style={{ marginLeft: 12 }}
                    onClick={() => this.setState({ contractQuoteId: q.id })}>
                    Review &amp; sign contract
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }
}

export default withStyles(s)(CompanyProjects);
